import type { FC } from 'react'
import { useRef } from 'react'

import type { TranslationLang, UpdateTranslationPayload } from '@skytech/types'
import { Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import {
  LANGUAGE_FLAGS,
  LOCALE_WITH_NAMES,
  getEntries,
  getInitialTranslationsValues,
} from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'

import { useAppContext } from '@/app/index.context'
import { useEditTranslationsPrivilege } from '@/hooks/user'
import { commonQueries } from '@/resources/queries'
import { commonTranslationResources } from '@/resources/translations'

import { useTranslation } from '../translations-hooks'

import './EditTranslationsForm.styles.scss'

export const EditTranslationsForm: FC<{ translationId: string; onClose: () => void }> = ({
  translationId,
  onClose,
}) => {
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const editTranslationsPrivilege = useEditTranslationsPrivilege()
  const { locale: userLocale } = useAppContext()
  const formRef = useRef<FormikProps<Record<TranslationLang, string>> | null>(null)
  const { data: translationList } = useQuery(
    commonQueries.translationList(),
    commonTranslationResources.getList,
    { staleTime: Infinity }
  )
  const translationsMutation = useMutation(commonTranslationResources.update, {
    onSuccess() {
      queryClient.invalidateQueries(commonQueries.translationList())
    },
  })
  return (
    <Formik
      innerRef={formRef}
      initialValues={getInitialTranslationsValues({ translationList, translationId })}
      onSubmit={(formValues, { resetForm }) => {
        const payload: UpdateTranslationPayload = []
        getEntries(formValues).forEach(([key, val]) => {
          const isDirty = formRef.current?.getFieldMeta(key).initialValue !== val
          if (!isDirty) return
          payload.push({
            locale: key,
            id: translationId,
            text: val || '',
          })
          const updatedInitialValues = getInitialTranslationsValues({
            translationList,
            translationId,
          })
          payload.forEach(({ locale, text }) => {
            updatedInitialValues[locale] = text
          })
          resetForm({ values: updatedInitialValues })
        })
        translationsMutation.mutate(payload)
      }}
    >
      <div className="edit-translations">
        <h2>{`${translate('Edit translations for')} '${translationId}'`}</h2>

        <Form>
          <div className="language-inputs">
            {getEntries(LANGUAGE_FLAGS).map(([locale, flag]) => {
              const isEnabled =
                editTranslationsPrivilege === 'CAN_EDIT_ALL_LANG' || locale === userLocale
              return (
                <Field key={locale} name={locale}>
                  {({ field, meta }: FieldProps) => (
                    <Textarea
                      isFullWidth
                      className={locale === userLocale ? 'is-user-lang' : ''}
                      id={locale}
                      isDisabled={!isEnabled}
                      label={`${LOCALE_WITH_NAMES[locale]} ${flag}`}
                      marginBottom={16}
                      error={meta.error}
                      touched={meta.touched}
                      {...field}
                    />
                  )}
                </Field>
              )
            })}
          </div>

          <footer>
            <Button variant="tertiary" disabled={translationsMutation.isLoading} onClick={onClose}>
              {translate('Cancel')}
            </Button>

            <Button type="submit" isLoading={translationsMutation.isLoading}>
              {translate('Submit')}
            </Button>
          </footer>
        </Form>
      </div>
    </Formik>
  )
}
